import { useEffect, useRef, useState } from 'react';

interface Position {
  lat: number;
  lng: number;
  bearing?: number;
}

export const useSmoothMarker = (
  targetLat: number | null,
  targetLng: number | null,
  targetBearing: number | null | undefined = null,
  animationDuration: number = 3000,
): Position | null => {
  const [currentPosition, setCurrentPosition] = useState<Position | null>(null);
  const animationRef = useRef<number | null>(null);
  const startPositionRef = useRef<Position | null>(null);
  const startTimeRef = useRef<number | null>(null);
  const previousTargetRef = useRef<{ lat: number; lng: number } | null>(null);
  const isInitializedRef = useRef<boolean>(false);

  useEffect(() => {
    if (targetLat === null || targetLng === null) {
      if (currentPosition !== null && isInitializedRef.current) {
        console.log('📍 useSmoothMarker: Target is null, keeping last position');
      }
      return;
    }

    const targetPosition: Position = { 
      lat: targetLat, 
      lng: targetLng,
      bearing: targetBearing !== null && targetBearing !== undefined ? targetBearing : undefined,
    };

    const previousTarget = previousTargetRef.current;
    const isNewTarget = !previousTarget || 
      Math.abs(previousTarget.lat - targetLat) > 0.0001 ||
      Math.abs(previousTarget.lng - targetLng) > 0.0001;

    if (currentPosition === null) {
      console.log('📍 useSmoothMarker: First initialization with target position:', {
        lat: targetPosition.lat,
        lng: targetPosition.lng,
        bearing: targetPosition.bearing,
      });
      setCurrentPosition(targetPosition);
      startPositionRef.current = targetPosition;
      previousTargetRef.current = { lat: targetLat, lng: targetLng };
      isInitializedRef.current = true;
      return;
    }

    if (isNewTarget && isInitializedRef.current) {
      const distanceMeters = Math.sqrt(
        Math.pow((currentPosition.lat - targetLat) * 111000, 2) +
        Math.pow((currentPosition.lng - targetLng) * 111000 * Math.cos(currentPosition.lat * Math.PI / 180), 2)
      );
      
      if (distanceMeters > 1000) {
        console.log('📍 useSmoothMarker: Large distance detected (>1km), jumping to target immediately:', {
          currentLat: currentPosition.lat,
          currentLng: currentPosition.lng,
          targetLat: targetPosition.lat,
          targetLng: targetPosition.lng,
          distanceMeters: Math.round(distanceMeters),
        });
        setCurrentPosition(targetPosition);
        startPositionRef.current = targetPosition;
        previousTargetRef.current = { lat: targetLat, lng: targetLng };
        if (animationRef.current) {
          cancelAnimationFrame(animationRef.current);
          animationRef.current = null;
        }
        return;
      }
      
      console.log('📍 useSmoothMarker: New target detected, will animate from current position:', {
        currentLat: currentPosition.lat,
        currentLng: currentPosition.lng,
        targetLat: targetPosition.lat,
        targetLng: targetPosition.lng,
        previousTarget,
        distanceMeters: Math.round(distanceMeters),
      });
      previousTargetRef.current = { lat: targetLat, lng: targetLng };
    } else if (isNewTarget) {
      console.log('📍 useSmoothMarker: Setting position immediately (not yet initialized):', {
        lat: targetPosition.lat,
        lng: targetPosition.lng,
      });
      setCurrentPosition(targetPosition);
      startPositionRef.current = targetPosition;
      previousTargetRef.current = { lat: targetLat, lng: targetLng };
      isInitializedRef.current = true;
      return;
    }

    if (
      Math.abs(currentPosition.lat - targetLat) < 0.00001 &&
      Math.abs(currentPosition.lng - targetLng) < 0.00001
    ) {
      if (targetBearing !== null && targetBearing !== undefined && 
          currentPosition.bearing !== targetBearing) {
        setCurrentPosition({ ...currentPosition, bearing: targetBearing });
      }
      previousTargetRef.current = { lat: targetLat, lng: targetLng };
      return;
    }

    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
    }

    startPositionRef.current = { ...currentPosition };
    const startBearing = currentPosition.bearing ?? targetBearing ?? 0;
    startTimeRef.current = Date.now();

    const animate = () => {
      const now = Date.now();
      const elapsed = now - (startTimeRef.current || now);
      const progress = Math.min(elapsed / animationDuration, 1);

      const lat =
        (startPositionRef.current?.lat || 0) +
        (targetPosition.lat - (startPositionRef.current?.lat || 0)) * progress;
      const lng =
        (startPositionRef.current?.lng || 0) +
        (targetPosition.lng - (startPositionRef.current?.lng || 0)) * progress;

      let bearing: number | undefined = undefined;
      if (targetBearing !== null && targetBearing !== undefined) {
        const targetBearingValue = targetBearing;
        let bearingDiff = targetBearingValue - startBearing;

        if (bearingDiff > 180) bearingDiff -= 360;
        if (bearingDiff < -180) bearingDiff += 360;

        bearing = startBearing + bearingDiff * progress;
        bearing = ((bearing % 360) + 360) % 360;
      }

      setCurrentPosition({ lat, lng, bearing });

      if (progress < 1) {
        animationRef.current = requestAnimationFrame(animate);
      } else {
        setCurrentPosition(targetPosition);
        animationRef.current = null;
      }
    };

    animationRef.current = requestAnimationFrame(animate);

    // Cleanup
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [targetLat, targetLng, targetBearing, animationDuration]);

  return currentPosition;
};
